import { Area, CurveType, Line, XYLabels } from '@unovis/ts'
import { ChartDataSet, XYRecord } from '~/utils/charts/unovis'
import { lineGradientId, seriesByType } from '~/utils/charts/zchart'

type BuildLineSeriesComponentsOptions = {
  chartType: string
  dataSets: Array<ChartDataSet>
  palette: Array<string>
  xyRecords: Array<XYRecord>
  x: (d: XYRecord) => number
}

export const buildLineSeriesComponents = (
  options: BuildLineSeriesComponentsOptions
): Array<unknown> => {
  const { chartType, dataSets, palette, xyRecords, x } = options
  const lineIndices = seriesByType(chartType, dataSets, 'line')
  if (!lineIndices.length) return []

  const lineAccessors = lineIndices.map((index) => (d: XYRecord) => d.values[index] ?? null)
  const lineColors = lineIndices.map((index) => palette[index] || palette[0])
  const lastPointAccessors = lineIndices.map((index) => (d: XYRecord) => {
    const lastIndex = xyRecords.length - 1
    if (lastIndex < 0) return Number.NaN
    const value = d.values[index]
    return d.x === lastIndex && value !== null ? value : Number.NaN
  })

  const areaComponents = lineIndices.map(
    (index) =>
      new Area<XYRecord>({
        x,
        y: (d: XYRecord) => d.values[index] ?? null,
        color: (_data: XYRecord[]) => `url(#${lineGradientId(index)})`,
        curveType: CurveType.Linear
      })
  )

  return [
    ...areaComponents,
    new Line<XYRecord>({
      x,
      y: lineAccessors,
      color: lineColors,
      lineWidth: 2,
      curveType: CurveType.Linear
    }),
    ...lastPointAccessors.map(
      (accessor, index) =>
        new XYLabels<XYRecord>({
          x,
          y: accessor,
          color: lineColors[index],
          backgroundColor: lineColors[index],
          label: '',
          labelFontSize: 8,
          clustering: false,
          xPositioning: 'data_space',
          yPositioning: 'data_space',
          cursor: null,
          excludeFromDomainCalculation: true
        })
    )
  ]
}
